<div class="text-fg w-full max-w-none p-6">
  <!-- Loading -->
  @if (loading()) {
    <div class="flex items-center justify-center gap-2 p-12 text-sm text-muted">
      <ui-spinner size="sm" />
      <span>Loading merchant...</span>
    </div>
  } @else if (error() && !merchant()) {
    <div class="p-8 text-center text-danger">
      <p>{{ error() }}</p>
      <button type="button" uiButton variant="outline" size="sm" class="mt-3" (click)="loadMerchant()">Retry</button>
    </div>
  } @else if (merchant(); as m) {
    <!-- Header -->
    <header class="mb-6">
      <button type="button" class="mb-3 inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[0.85rem] text-muted hover:text-primary" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="m-0 flex items-center gap-2 text-2xl font-bold text-fg">
            <ng-icon name="lucideStore" size="22" />
            {{ m.name }}
          </h1>
          @if (m.legal_name && m.legal_name !== m.name) {
            <span class="mt-0.5 text-[0.85rem] text-muted">{{ m.legal_name }}</span>
          }
          <div class="mt-2 flex items-center gap-2">
            <ui-badge [variant]="m.is_active ? 'success' : 'danger'">{{ m.is_active ? 'Active' : 'Inactive' }}</ui-badge>
            <ui-badge>{{ m.country_name }} ({{ m.country_code }})</ui-badge>
          </div>
        </div>
        <div class="flex gap-2">
          <button type="button" uiButton variant="outline" size="sm" (click)="refreshAll()" title="Refresh">
            <ng-icon name="lucideRefreshCw" size="16" />
          </button>
        </div>
      </div>
    </header>

    <!-- ─── KPI Cards ──────────────────── -->
    @if (stats(); as s) {
      <section class="mb-6">
        <div class="grid grid-cols-[repeat(auto-fill,minmax(175px,1fr))] gap-3">
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-info-bg text-info-fg"><ng-icon name="lucideBanknote" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Total Transactions</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ getTotalTx() }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-success-bg text-success-fg"><ng-icon name="lucideChartColumn" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Success Volume</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ formatAmount(getTotalSuccessVol()) }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideShieldCheck" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Success Rate</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ getSuccessRate() }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-warning-bg text-warning-fg"><ng-icon name="lucideWallet" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Available Balance</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ formatAmount(getTotalAvailableBalance()) }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-secondary"><ng-icon name="lucideServer" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Applications</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ s.applications.active }} / {{ s.applications.total }}</span>
            </div>
          </div>
          <div class="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3.5">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-danger-bg text-danger-fg"><ng-icon name="lucideKey" size="20" /></div>
            <div class="flex min-w-0 flex-col">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">API Keys</span>
              <span class="truncate text-[1.1rem] font-bold text-fg">{{ s.api_keys.active }} / {{ s.api_keys.total }}</span>
            </div>
          </div>
        </div>

        <!-- Period selector -->
        <div class="mt-3 flex justify-end">
          <select uiInput size="sm" class="!w-auto cursor-pointer" [(ngModel)]="statsPeriod" (ngModelChange)="onPeriodChange()">
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </section>
    }

    <!-- ─── Merchant Info ──────────────── -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
        <ng-icon name="lucideStore" size="16" />
        Merchant Details
      </h3>
      <div class="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
        <div class="flex flex-col gap-0.5">
          <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">ID</span>
          <span class="inline-flex cursor-pointer items-center gap-1 text-[0.85rem] font-medium text-fg hover:text-primary" (click)="copyToClipboard(m.id)" title="Copy">
            <code class="rounded bg-surface-muted px-1.5 py-0.5 text-[0.78rem]">{{ m.id }}</code>
            <ng-icon name="lucideClipboard" size="12" />
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">
            <ng-icon name="lucideMail" size="12" />
            Email
          </span>
          <span class="text-[0.85rem] font-medium text-fg">{{ m.email }}</span>
        </div>
        @if (m.phone_number) {
          <div class="flex flex-col gap-0.5">
            <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">
              <ng-icon name="lucidePhone" size="12" />
              Phone
            </span>
            <span class="text-[0.85rem] font-medium text-fg">{{ m.phone_number }}</span>
          </div>
        }
        <div class="flex flex-col gap-0.5">
          <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">
            <ng-icon name="lucideGlobe" size="12" />
            Country
          </span>
          <span class="text-[0.85rem] font-medium text-fg">{{ m.country_name }} ({{ m.country_code }})</span>
        </div>
        @if (m.registration_number) {
          <div class="flex flex-col gap-0.5">
            <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Registration #</span>
            <span class="text-[0.85rem] font-medium text-fg">{{ m.registration_number }}</span>
          </div>
        }
        @if (m.tax_id) {
          <div class="flex flex-col gap-0.5">
            <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Tax ID</span>
            <span class="text-[0.85rem] font-medium text-fg">{{ m.tax_id }}</span>
          </div>
        }
        <div class="flex flex-col gap-0.5">
          <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Created</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ formatDate(m.created_at) }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Updated</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ formatDate(m.updated_at) }}</span>
        </div>
      </div>
    </section>

    <!-- ─── Webhook Configuration ─────── -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-center justify-between [&>h3]:mb-0">
        <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideBell" size="16" />
          Webhook Configuration
        </h3>
        @if (!webhookEditing()) {
          <button type="button" uiButton variant="outline" size="sm" (click)="startWebhookEdit()">
            <ng-icon name="lucideSettings" size="14" />
            <span>Configure</span>
          </button>
        }
      </div>

      @if (webhookSuccess()) {
        <ui-alert variant="success">{{ webhookSuccess() }}</ui-alert>
      }
      @if (webhookError()) {
        <ui-alert variant="danger">
          <div class="flex items-center gap-2">
            <span class="flex-1">{{ webhookError() }}</span>
            <button type="button" class="cursor-pointer border-none bg-transparent p-0.5 text-inherit opacity-80 hover:opacity-100" (click)="webhookError.set(null)">
              <ng-icon name="lucideX" size="14" />
            </button>
          </div>
        </ui-alert>
      }

      <!-- Generated secret banner (shown once after regeneration) -->
      @if (generatedSecret(); as secret) {
        <div class="mb-3 flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-bg px-4 py-3">
          <div class="flex items-center gap-1.5 text-[0.8rem] text-warning-fg">
            <ng-icon name="lucideTriangleAlert" size="16" />
            <strong>Copy your webhook secret now — it will not be shown again.</strong>
          </div>
          <div class="flex items-center gap-2">
            <code class="flex-1 break-all rounded-md border border-warning-border bg-card px-2.5 py-1.5 font-mono text-[0.78rem] text-fg">{{ secret }}</code>
            <button type="button" uiButton variant="outline" size="sm" (click)="copyToClipboard(secret)" title="Copy">
              <ng-icon name="lucideClipboard" size="14" />
              <span>Copy</span>
            </button>
          </div>
          <button type="button" uiButton variant="outline" size="sm" class="self-start" (click)="dismissGeneratedSecret()">
            I've copied the secret
          </button>
        </div>
      }

      @if (!webhookEditing()) {
        <!-- Read-only view -->
        <div class="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
          <div class="flex flex-col gap-0.5 sm:col-span-2">
            <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Webhook URL</span>
            @if (m.webhook_url) {
              <span class="inline-flex cursor-pointer items-center gap-1 text-[0.85rem] font-medium text-fg hover:text-primary" (click)="copyToClipboard(m.webhook_url)" title="Copy">
                <code class="break-all rounded bg-surface-muted px-1.5 py-0.5 text-[0.78rem]">{{ m.webhook_url }}</code>
                <ng-icon name="lucideClipboard" size="12" />
              </span>
            } @else {
              <span class="text-[0.85rem] font-medium italic text-muted">Not configured</span>
            }
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="flex items-center gap-1 text-[0.68rem] font-medium uppercase tracking-wider text-muted">Webhook Secret</span>
            <div class="flex items-center gap-2">
              @if (m.has_webhook_secret) {
                <ui-badge variant="success">Configured</ui-badge>
              } @else {
                <ui-badge variant="danger">Not generated</ui-badge>
              }
              <button
                type="button"
                uiButton
                variant="outline"
                size="sm"
                [disabled]="webhookRegenerating()"
                (click)="regenerateSecret()"
                title="{{ m.has_webhook_secret ? 'Regenerate secret (replaces current)' : 'Generate a new secret' }}"
              >
                @if (webhookRegenerating()) {
                  <ui-spinner size="sm" />
                } @else {
                  <ng-icon name="lucideKey" size="12" />
                }
                <span>{{ m.has_webhook_secret ? 'Regenerate' : 'Generate' }}</span>
              </button>
            </div>
          </div>
        </div>
        <p class="mt-3 text-[0.78rem] text-muted">
          Webhook events will be forwarded to this URL. The secret is used to sign the payload
          (HMAC-SHA256) so the merchant can verify authenticity.
        </p>
      } @else {
        <!-- Editable form (URL only) -->
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <label for="webhookUrl" class="text-[0.78rem] font-semibold text-fg-secondary">Webhook URL</label>
            <input
              id="webhookUrl"
              type="url"
              uiInput
              [(ngModel)]="webhookUrlEdit"
              placeholder="https://your-domain.com/webhooks"
            />
            <span class="text-[0.72rem] leading-snug text-muted">The URL where webhook events will be sent via POST requests.</span>
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <button type="button" uiButton variant="outline" (click)="cancelWebhookEdit()" [disabled]="webhookSaving()">
              Cancel
            </button>
            <button type="button" uiButton variant="primary" (click)="saveWebhookConfig()" [disabled]="webhookSaving()">
              @if (webhookSaving()) {
                <ui-spinner size="sm" />
              }
              <span>Save</span>
            </button>
          </div>
        </div>
      }
    </section>

    <!-- ─── Applications ──────────────── -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-center justify-between [&>h3]:mb-0">
        <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideServer" size="16" />
          Applications
        </h3>
      </div>
      @if (apps().length === 0) {
        <ui-empty-state title="No applications for this merchant." />
      } @else {
        <div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
          @for (app of apps(); track app.id) {
            <a [routerLink]="shellRoutes.commands('manage', 'merchant-applications', app.id)" class="group relative block rounded-lg border border-border bg-card p-3.5 text-inherit no-underline transition hover:border-primary hover:shadow-md">
              <div class="mb-1.5 flex items-center justify-between">
                <span class="text-[0.88rem] font-semibold text-fg">{{ app.name }}</span>
                <ui-badge [variant]="getEnvVariant(app.environment)" class="uppercase">{{ app.environment }}</ui-badge>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-mono text-[0.72rem] text-muted">{{ app.client_id }}</span>
                <div class="flex items-center gap-2">
                  <ui-badge [variant]="app.is_active ? 'success' : 'danger'">{{ app.is_active ? 'Active' : 'Inactive' }}</ui-badge>
                  <span class="text-[0.72rem] text-muted">{{ app.active_key_count }} keys</span>
                </div>
              </div>
              <ng-icon name="lucideExternalLink" size="14" class="absolute right-2.5 top-2.5 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
          }
        </div>
      }
    </section>

    <!-- ─── Webhook Events Quick Link ──── -->
    @if (apps().length > 0) {
      <section class="mb-5 rounded-xl border border-border bg-card p-5">
        <div class="mb-3 flex items-center justify-between [&>h3]:mb-0">
          <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
            <ng-icon name="lucideBell" size="16" />
            Webhooks
          </h3>
          <a [routerLink]="shellRoutes.commands('manage', 'webhook-events')" uiButton variant="outline" size="sm">
            <ng-icon name="lucideBell" size="14" />
            <span>View Webhook Events</span>
          </a>
        </div>
        <p class="m-0 text-[0.78rem] text-muted">View all webhook events received for this merchant's applications.</p>
      </section>
    }

    <!-- ─── Wallets ────────────────────── -->
    @if (stats(); as s) {
      @if (s.wallets.total > 0) {
        <section class="mb-5 rounded-xl border border-border bg-card p-5">
          <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
            <ng-icon name="lucideWallet" size="16" />
            Wallets <span class="ms-1 text-[0.82rem] font-bold text-muted">{{ s.wallets.total }}</span>
          </h3>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2">
            @for (group of getMarketWalletGroups(); track group.marketCode) {
              <div class="flex flex-col gap-2 rounded-lg border border-border bg-card p-2.5">
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="rounded bg-row-hover px-1.5 py-0.5 text-[0.72rem] font-bold text-primary">{{ group.marketCode }}</span>
                  <span class="text-[0.72rem] font-medium text-muted">{{ marketLabel(group) }}</span>
                  <span class="ms-auto text-[0.68rem] font-semibold text-muted">
                    {{ group.walletCount }} {{ group.walletCount === 1 ? 'wallet' : 'wallets' }}
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-3">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Balance</span>
                    <span class="text-[0.85rem] font-bold tabular-nums text-fg">{{ formatAmount(group.totalBalance) }}</span>
                  </div>
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Ledger</span>
                    <span class="text-[0.85rem] font-bold tabular-nums text-fg">{{ formatAmount(group.totalLedger) }}</span>
                  </div>
                  @if (group.hasMismatch) {
                    <ui-badge variant="warning" class="ms-auto !text-[0.62rem]">Mismatch</ui-badge>
                  }
                </div>
                <div class="flex flex-col gap-1 border-t border-border pt-1.5">
                  @for (walletCard of group.wallets; track walletCard.walletId) {
                    <a class="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[0.78rem] text-inherit no-underline transition-colors hover:bg-surface-muted" [routerLink]="walletRoute(walletCard.walletId)">
                      <span class="min-w-0 flex-1 truncate font-semibold text-fg-secondary">
                        {{ getWalletLabel(walletCard, group.wallets) }}
                      </span>
                      <span class="font-bold tabular-nums text-fg">{{ formatAmount(walletCard.walletBalance) }}</span>
                      @if (walletCard.isTaggedMismatch) {
                        <span class="inline-flex size-4 items-center justify-center rounded-full bg-warning-bg text-[0.6rem] font-extrabold text-warning-fg">!</span>
                      }
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        </section>
      }
    }

    <!-- ─── Transaction Breakdown ──────── -->
    @if (stats(); as s) {
      <section class="mb-5 rounded-xl border border-border bg-card p-5">
        <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideChartColumn" size="16" />
          Transaction Activity
        </h3>
        <div class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
          @for (type of ['deposits', 'payouts', 'refunds', 'remittances']; track type) {
            <div class="rounded-lg border border-border p-3">
              <span class="mb-1.5 block text-[0.78rem] font-semibold capitalize text-fg-secondary">{{ type | titlecase }}</span>
              <div class="flex flex-col gap-1">
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg">
                  <span>Successful total</span>
                  <strong>{{ $any(s.transactions)[type].total }}</strong>
                </div>
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-success">
                  <span>Success</span>
                  <strong>{{ $any(s.transactions)[type].success }}</strong>
                </div>
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-danger">
                  <span>Failed</span>
                  <strong>{{ $any(s.transactions)[type].failed }}</strong>
                </div>
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-warning">
                  <span>Pending</span>
                  <strong>{{ $any(s.transactions)[type].pending }}</strong>
                </div>
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg mt-0.5 border-t border-border pt-1">
                  <span>Volume</span>
                  <strong>{{ formatAmount($any(s.transactions)[type].success_amount) }}</strong>
                </div>
              </div>
            </div>
          }
        </div>
      </section>
    }

    @if (feeSummary(); as fs) {
      <section class="mb-5 rounded-xl border border-border bg-card p-5">
        <div class="mb-3 flex items-center justify-between [&>h3]:mb-0">
          <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
            <ng-icon name="lucideBanknote" size="16" />
            Merchant Fee Breakdown
          </h3>
          <a [routerLink]="shellRoutes.commands('fees-hub')" uiButton variant="outline" size="sm">Open Fees Hub</a>
        </div>
        <div class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
          <div class="rounded-lg border border-border p-3">
            <span class="mb-1.5 block text-[0.78rem] font-semibold capitalize text-fg-secondary">Merchant Paid (All fees)</span>
            <div class="flex flex-col gap-1">
              @for (entry of fs.merchant_paid_by_currency | keyvalue; track entry.key) {
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg mt-0.5 border-t border-border pt-1">
                  <span>{{ entry.key }}</span>
                  <strong>{{ formatMoney(entry.value, entry.key) }}</strong>
                </div>
              }
            </div>
          </div>
          <div class="rounded-lg border border-border p-3">
            <span class="mb-1.5 block text-[0.78rem] font-semibold capitalize text-fg-secondary">Gross Gain</span>
            <div class="flex flex-col gap-1">
              @for (entry of fs.gross_gain_by_currency | keyvalue; track entry.key) {
                <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg mt-0.5 border-t border-border pt-1">
                  <span>{{ entry.key }}</span>
                  <strong>{{ formatMoney(entry.value, entry.key) }}</strong>
                </div>
              }
            </div>
          </div>
        </div>
      </section>
    }

    @if (feeConfigs().length > 0) {
      <section class="mb-5 rounded-xl border border-border bg-card p-5">
        <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideSettings" size="16" />
          Fee Configurations (Merchant scoped)
        </h3>
        <div class="flex flex-col gap-1">
          @for (cfg of feeConfigs(); track cfg.id) {
            <div class="flex items-center gap-3 rounded-md border border-border px-2.5 py-2 text-[0.78rem]">
              <span class="min-w-20 font-medium text-fg-secondary">{{ cfg.fee_type }} · {{ cfg.amount_type }} {{ cfg.value }}</span>
              <span class="flex-1 font-mono text-[0.72rem] text-muted">{{ cfg.operation_type || 'ALL' }} · {{ cfg.scope }}</span>
              <span class="whitespace-nowrap text-[0.72rem] text-muted">{{ cfg.currency || '—' }}</span>
            </div>
          }
        </div>
      </section>
    }

    <!-- ─── Recent Audit ──────────────── -->
    @if (stats(); as s) {
      @if (s.recent_audit.length > 0) {
        <section class="mb-5 rounded-xl border border-border bg-card p-5">
          <h3 class="m-0 mb-3 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
            <ng-icon name="lucideShieldCheck" size="16" />
            Recent Activity
          </h3>
          <div class="flex flex-col gap-1">
            @for (entry of s.recent_audit; track entry.id) {
              <div class="flex items-center gap-3 rounded-md border border-border px-2.5 py-2 text-[0.78rem]">
                <span class="min-w-20 font-medium text-fg-secondary">{{ entry.action }}</span>
                <span class="flex-1 font-mono text-[0.72rem] text-muted">{{ entry.entity_type }} / {{ entry.entity_id | slice:0:8 }}...</span>
                <span class="whitespace-nowrap text-[0.72rem] text-muted">{{ formatDate(entry.timestamp) }}</span>
              </div>
            }
          </div>
        </section>
      }
    }
  }

  <!-- Copy toast -->
  @if (copyToast()) {
    <div class="fixed bottom-8 left-1/2 z-[9999] -translate-x-1/2 rounded-lg bg-fg px-4 py-2 text-[0.8rem] font-medium text-card shadow-lg">Copied!</div>
  }
</div>
